import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { catchError, debounceTime, EMPTY, filter, map, merge, mergeMap, of, switchMap, tap, withLatestFrom } from 'rxjs';
import { AppState } from '../../common/state/root';
import { AlertActions } from '../../common/state/alert/alert.actions';
import { AlertService } from '../../common/services/alert/alert.service';
import { selectPagination } from '../../common/state/alert/alert.selectors';
import { selectUser } from '../../common/state/user/user.selectors';
import { clearAggregateState, loadAggregateState, storeAggregateState } from '../../common/state/alert/helpers/filter.helpers';
import { LocalStorageService } from '../../common/services/local-storage/local-storage.service';
import { AlertAggregateService } from '../services/alert-aggregate.service';
import { AggregateActions } from './aggregate.actions';
import {
  selectAggregateRequest,
  selectAggregateSpecs,
  selectGroupBy,
  selectIsAggregateActive,
  selectIsAtLeafLevel,
  selectLeafFilterGroups,
} from './aggregate.selectors';

@Injectable()
export class AggregateEffects {
  private actions$ = inject(Actions);
  private store$ = inject(Store<AppState>);
  private aggregateService = inject(AlertAggregateService);
  private alertService = inject(AlertService);
  private storage = inject(LocalStorageService);

  clearSelectionOnDrilldownBack$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AggregateActions.popDrilldown,
        AggregateActions.clearDrilldown,
        AggregateActions.truncateDrilldown,
      ),
      map(() => AlertActions.clearSelectedAlerts()),
    ),
  );

  runAggregate$ = createEffect(() =>
    merge(
      this.actions$.pipe(
        ofType(
          AggregateActions.runAggregate,
          AggregateActions.loadNextPage,
          AggregateActions.pushDrilldown,
          AggregateActions.popDrilldown,
          AggregateActions.clearDrilldown,
          AggregateActions.truncateDrilldown,
          AggregateActions.setPagination,
        ),
      ),
      this.actions$.pipe(
        ofType(AlertActions.setFilter),
        withLatestFrom(this.store$.select(selectIsAggregateActive)),
        filter(([, isActive]) => isActive),
        map(([action]) => action),
      ),
    ).pipe(
      debounceTime(0),
      withLatestFrom(
        this.store$.select(selectIsAggregateActive),
        this.store$.select(selectIsAtLeafLevel),
        this.store$.select(selectAggregateRequest),
        this.store$.select(selectLeafFilterGroups),
        this.store$.select(selectPagination),
      ),
      filter(([, isActive, isLeaf, request]) => isActive && (isLeaf || request.group_by[0] != null)),
      switchMap(([, , isLeaf, request, leafFilter, page]) => {
        if (isLeaf) {
          return this.alertService
            .filterAlertsByGroups(leafFilter.groups, page, leafFilter.ordering)
            .pipe(
              mergeMap((res) => [
                // authoritative: the leaf/drilldown filter (tag, camera, etc.) is applied server-side
                // and cannot be re-evaluated by alertMatchesCurrentFilter, so the response is the
                // complete set — carrying over in-flight sibling-group alerts would inflate the grid.
                AlertActions.loadAlertsSuccess({ alerts: res.alerts, total: res.total, resetState: true, authoritative: true }),
                AggregateActions.leafAlertsLoaded(),
              ]),
              catchError(() => of(AlertActions.loadAlertsFailure({ error: new Error('Leaf alert fetch failed') }))),
            );
        }
        return this.aggregateService.aggregate(request).pipe(
          map((response) => AggregateActions.runAggregateSuccess({ response })),
          catchError((err) =>
            of(AggregateActions.runAggregateFailure({ error: err?.message ?? 'Unknown error' })),
          ),
        );
      }),
    ),
  );

  // Rehydrate a persisted aggregate config and replay the actions that activate it.
  restoreState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AggregateActions.restoreState),
      withLatestFrom(this.store$.select(selectUser)),
      mergeMap(([{ page }, user]) => {
        if (user == null) {
          return EMPTY;
        }
        const restored = loadAggregateState(user, page, this.storage);
        if (restored == null) {
          return EMPTY;
        }
        const actions: Action[] = [
          AggregateActions.openAggregate(),
          AggregateActions.setGroupBy({ groupBy: restored.groupBy }),
        ];
        if (restored.metrics.length > 0) {
          actions.push(AggregateActions.setAggregates({ aggregates: restored.metrics }));
        }
        actions.push(AggregateActions.runAggregate());
        return actions;
      }),
    ),
  );

  persistState$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AggregateActions.persistState),
        withLatestFrom(
          this.store$.select(selectUser),
          this.store$.select(selectGroupBy),
          this.store$.select(selectAggregateSpecs),
        ),
        tap(([{ page }, user, groupBy, metrics]) => {
          if (user != null) {
            storeAggregateState(user, page, groupBy, metrics, this.storage);
          }
        }),
      ),
    { dispatch: false },
  );

  clearPersistedState$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AggregateActions.clearPersistedState),
        withLatestFrom(this.store$.select(selectUser)),
        tap(([{ page }, user]) => {
          if (user != null) {
            clearAggregateState(user, page, this.storage);
          }
        }),
      ),
    { dispatch: false },
  );
}
